/**
 * Tabella voci catalogo di un fornitore.
 *
 * Usata nella pagina FornitoreDetail come contenuto del tab "Catalogo".
 * Include form inline per aggiunta rapida, modifica inline e soft delete.
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@floraltech/ui';
import { Pencil, Trash2, X, Check } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';

interface CatalogoItem {
  id: string;
  sku: string;
  descrizione: string | null;
  prezzo_unitario: number | null;
  moq: number | null;
  sla_consegna_giorni: number | null;
  stato: string;
}

interface CatalogoTableProps {
  /** UUID del soggetto fornitore. */
  fornitoreId: string;
}

/** Headers comuni per le fetch autenticate. */
const authHeaders = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
  'Content-Type': 'application/json',
});

/**
 * Tabella catalogo con aggiunta rapida, modifica inline e soft delete.
 */
const CatalogoTable: React.FC<CatalogoTableProps> = ({ fornitoreId }: CatalogoTableProps) => {
  const [items, setItems] = useState<CatalogoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form aggiunta rapida
  const [showForm, setShowForm] = useState(false);
  const [newSku, setNewSku] = useState('');
  const [newDescrizione, setNewDescrizione] = useState('');
  const [newPrezzo, setNewPrezzo] = useState('');
  const [newMoq, setNewMoq] = useState('');
  const [newSla, setNewSla] = useState('');
  const [saving, setSaving] = useState(false);

  // Modifica inline
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editSku, setEditSku] = useState('');
  const [editDescrizione, setEditDescrizione] = useState('');
  const [editPrezzo, setEditPrezzo] = useState('');
  const [editMoq, setEditMoq] = useState('');
  const [editSla, setEditSla] = useState('');

  const baseUrl = `/api/v1/fornitori/${fornitoreId}/catalogo`;

  const carica = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(baseUrl, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
        },
      });
      if (!response.ok) throw new Error(`Errore ${response.status}`);
      const data: CatalogoItem[] = await response.json();
      setItems(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  }, [baseUrl]);

  useEffect(() => {
    void carica();
  }, [carica]);

  /** Crea una nuova voce catalogo. */
  const handleAdd = async () => {
    if (!newSku.trim()) return;
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        sku: newSku.trim(),
        descrizione: newDescrizione.trim() || null,
        prezzo_unitario: newPrezzo ? Number(newPrezzo) : null,
        moq: newMoq ? Number(newMoq) : null,
        sla_consegna_giorni: newSla ? Number(newSla) : null,
      };
      const response = await fetch(baseUrl, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`Errore ${response.status}`);
      setNewSku('');
      setNewDescrizione('');
      setNewPrezzo('');
      setNewMoq('');
      setNewSla('');
      setShowForm(false);
      void carica();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  /** Entra in modalita modifica su una riga. */
  const startEdit = (item: CatalogoItem) => {
    setEditingId(item.id);
    setEditSku(item.sku);
    setEditDescrizione(item.descrizione ?? '');
    setEditPrezzo(item.prezzo_unitario != null ? String(item.prezzo_unitario) : '');
    setEditMoq(item.moq != null ? String(item.moq) : '');
    setEditSla(item.sla_consegna_giorni != null ? String(item.sla_consegna_giorni) : '');
  };

  /** Salva la modifica inline con PATCH. */
  const handleSaveEdit = async () => {
    if (!editingId || !editSku.trim()) return;
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        sku: editSku.trim(),
        descrizione: editDescrizione.trim() || null,
        prezzo_unitario: editPrezzo ? Number(editPrezzo) : null,
        moq: editMoq ? Number(editMoq) : null,
        sla_consegna_giorni: editSla ? Number(editSla) : null,
      };
      const response = await fetch(`${baseUrl}/${editingId}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`Errore ${response.status}`);
      setEditingId(null);
      void carica();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  /** Soft delete di una voce catalogo. */
  const handleDelete = async (itemId: string) => {
    try {
      const response = await fetch(`${baseUrl}/${itemId}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!response.ok) throw new Error(`Errore ${response.status}`);
      void carica();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore eliminazione');
    }
  };

  return (
    <Card>
      <CardContent className="p-0">
        {loading && (
          <p className="p-6 text-center text-sm text-muted-foreground">
            Caricamento…
          </p>
        )}
        {error && (
          <p className="p-6 text-center text-sm text-destructive">{error}</p>
        )}
        {!loading && !error && (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>SKU</TableHead>
                  <TableHead>Descrizione</TableHead>
                  <TableHead className="text-right">Prezzo</TableHead>
                  <TableHead className="text-right">MOQ</TableHead>
                  <TableHead className="text-right">SLA (gg)</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead className="w-24" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 && !showForm && (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="text-center text-sm text-muted-foreground"
                    >
                      Nessuna voce nel catalogo
                    </TableCell>
                  </TableRow>
                )}
                {items.map((item: CatalogoItem) =>
                  editingId === item.id ? (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Input
                          value={editSku}
                          onChange={(e) => setEditSku(e.target.value)}
                          className="h-8 w-28 font-mono text-sm"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          value={editDescrizione}
                          onChange={(e) => setEditDescrizione(e.target.value)}
                          className="h-8 w-40"
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <Input
                          type="number"
                          step="0.0001"
                          value={editPrezzo}
                          onChange={(e) => setEditPrezzo(e.target.value)}
                          className="ml-auto h-8 w-24 text-right font-mono"
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <Input
                          type="number"
                          value={editMoq}
                          onChange={(e) => setEditMoq(e.target.value)}
                          className="ml-auto h-8 w-20 text-right"
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <Input
                          type="number"
                          value={editSla}
                          onChange={(e) => setEditSla(e.target.value)}
                          className="ml-auto h-8 w-16 text-right"
                        />
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            item.stato === 'attivo' ? 'default' : 'secondary'
                          }
                        >
                          {item.stato === 'attivo' ? 'Attivo' : 'Disattivato'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            onClick={handleSaveEdit}
                            disabled={saving}
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            onClick={() => setEditingId(null)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow key={item.id}>
                      <TableCell className="font-mono text-sm">
                        {item.sku}
                      </TableCell>
                      <TableCell>{item.descrizione ?? '—'}</TableCell>
                      <TableCell className="text-right font-mono">
                        {item.prezzo_unitario != null
                          ? `€ ${item.prezzo_unitario.toFixed(4)}`
                          : '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        {item.moq ?? '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        {item.sla_consegna_giorni ?? '—'}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            item.stato === 'attivo' ? 'default' : 'secondary'
                          }
                        >
                          {item.stato === 'attivo' ? 'Attivo' : 'Disattivato'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            onClick={() => startEdit(item)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-destructive hover:text-destructive"
                            onClick={() => handleDelete(item.id)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                )}
              </TableBody>
            </Table>

            {/* Form aggiunta rapida */}
            {showForm && (
              <div className="flex flex-wrap items-end gap-3 border-t p-4">
                <Input
                  placeholder="SKU"
                  value={newSku}
                  onChange={(e) => setNewSku(e.target.value)}
                  className="w-28"
                />
                <Input
                  placeholder="Descrizione"
                  value={newDescrizione}
                  onChange={(e) => setNewDescrizione(e.target.value)}
                  className="w-40"
                />
                <Input
                  placeholder="Prezzo"
                  type="number"
                  step="0.0001"
                  value={newPrezzo}
                  onChange={(e) => setNewPrezzo(e.target.value)}
                  className="w-24"
                />
                <Input
                  placeholder="MOQ"
                  type="number"
                  value={newMoq}
                  onChange={(e) => setNewMoq(e.target.value)}
                  className="w-20"
                />
                <Input
                  placeholder="SLA (gg)"
                  type="number"
                  value={newSla}
                  onChange={(e) => setNewSla(e.target.value)}
                  className="w-20"
                />
                <Button size="sm" onClick={handleAdd} disabled={saving}>
                  {saving ? 'Salva…' : 'Aggiungi'}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowForm(false)}
                >
                  Annulla
                </Button>
              </div>
            )}

            {!showForm && (
              <div className="border-t p-4">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowForm(true)}
                >
                  Aggiungi voce
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default CatalogoTable;
